import { LEAD_GENERATION_INTERACTION_TYPES, ANALYTICS_EVENTS } from 'components/analytics/types';
import { appEnvironment } from 'utils/environment';
import { ALLOWED_OVERRIDE_SUBDOMAINS } from 'utils/pageContext';
import { ButtonCtaLevel } from 'components/shared/Buttons/BaseButton';
import { getCookie } from 'cookies-next';
import { COOKIE_KEY as DEVICE_ID_COOKIE } from '@1debit/device-intelligence';
import { LAST_CAMPAIGN_COOKIE } from './cookies';
import { getPersistedAttribution } from 'utils/attributionPersistence';
import { addReferralParamsToUrl } from './referralHelpers';
import { addJointCheckingParamsToUrl } from './jointCheckingHelpers';

export interface EnrollmentUrlContext {
  persistedAttribution?: Record<string, string>;
  pageQueryParams?: Record<string, string>;
}

function isEnrollmentUrl(href: string): boolean {
  return !!href.match(/((member|app)((-(qa|dev\d))?)\.chime|test\.1debit)\.com\/(signup|enroll)(\/[^?#]*)?(?:\?|#|$)/);
}

function isOutboundLink(href: string): boolean {
  return !isInternalLink(href) && !isEnrollmentUrl(href) && !isLoginLink(href) && !isStoreUrl(href);
}

export function isLoginLink(href: string): boolean {
  return !!href.match(/((app|member)((-(qa|dev\d))?)\.chime)\.com\/(login|users\/sign_in)/);
}

function isStoreUrl(href: string): boolean {
  return !!href.match(/(play\.google\.com|apps\.apple\.com)/);
}

function isEngagementLink(href: string): boolean {
  return (isInternalLink(href) || isStoreUrl(href)) && !isEnrollmentUrl(href);
}

type LinkInteractionType =
  | typeof LEAD_GENERATION_INTERACTION_TYPES.OUTBOUND_LINK_TAPPED
  | typeof LEAD_GENERATION_INTERACTION_TYPES.INTERNAL_LINK_TAPPED
  | typeof LEAD_GENERATION_INTERACTION_TYPES.ENGAGEMENT_LINK_TAPPED
  | typeof LEAD_GENERATION_INTERACTION_TYPES.ENROLLMENT_CTA_TAPPED
  | typeof LEAD_GENERATION_INTERACTION_TYPES.MODULE_ELEMENT_TAPPED
  | typeof LEAD_GENERATION_INTERACTION_TYPES.SECONDARY_CTA_CLICKED
  | typeof ANALYTICS_EVENTS.LOGIN_LINK_TAPPED;

function getInteractionType(
  href: string,
  asButton: boolean,
  isSubnav: boolean,
  ctaType: ButtonCtaLevel,
): LinkInteractionType {
  if (asButton) {
    if (ctaType === 'secondary') {
      return LEAD_GENERATION_INTERACTION_TYPES.SECONDARY_CTA_CLICKED;
    }

    if (isOutboundLink(href)) {
      return LEAD_GENERATION_INTERACTION_TYPES.OUTBOUND_LINK_TAPPED;
    }

    if (isEnrollmentUrl(href)) {
      return LEAD_GENERATION_INTERACTION_TYPES.ENROLLMENT_CTA_TAPPED;
    }

    // All other links (internal, store, etc.) are treated as engagement links
    return LEAD_GENERATION_INTERACTION_TYPES.ENGAGEMENT_LINK_TAPPED;
  }

  if (isEnrollmentUrl(href)) {
    return LEAD_GENERATION_INTERACTION_TYPES.ENROLLMENT_CTA_TAPPED;
  }

  // Subnav links are treated as module elements
  if (isSubnav) return LEAD_GENERATION_INTERACTION_TYPES.MODULE_ELEMENT_TAPPED;

  return isInternalLink(href)
    ? LEAD_GENERATION_INTERACTION_TYPES.INTERNAL_LINK_TAPPED
    : LEAD_GENERATION_INTERACTION_TYPES.OUTBOUND_LINK_TAPPED;
}

function getCTAType(
  interactionType: LinkInteractionType,
  asButton: boolean,
): 'outbound' | 'internal' | 'enrollment' | 'engagement' | 'secondary' | 'unknown' {
  if (asButton) {
    switch (interactionType) {
      case LEAD_GENERATION_INTERACTION_TYPES.ENROLLMENT_CTA_TAPPED:
        return 'enrollment';
      case LEAD_GENERATION_INTERACTION_TYPES.ENGAGEMENT_LINK_TAPPED:
        return 'engagement';
      case LEAD_GENERATION_INTERACTION_TYPES.OUTBOUND_LINK_TAPPED:
        return 'outbound';
      case LEAD_GENERATION_INTERACTION_TYPES.SECONDARY_CTA_CLICKED:
        return 'secondary';
      default:
        return 'unknown';
    }
  }

  if (interactionType === LEAD_GENERATION_INTERACTION_TYPES.ENROLLMENT_CTA_TAPPED) {
    return 'enrollment';
  }

  return interactionType === LEAD_GENERATION_INTERACTION_TYPES.OUTBOUND_LINK_TAPPED ? 'outbound' : 'internal';
}

function getSecondaryCTAIndex(element: HTMLAnchorElement) {
  // get all ctas on the page
  const ctas = document.querySelectorAll('.secondary-cta');
  return Array.from(ctas).indexOf(element);
}

/**
 * Check if a URL is an internal link (chime.com, 1debit.com, or relative path)
 * This function securely checks for internal domains to prevent subdomain spoofing attacks
 */
function isInternalLink(href: string): boolean {
  // Handle relative paths and fragments as internal. Protocol-relative URLs
  // ('//host/path') are NOT relative paths — they carry a host and must fall
  // through to the hostname check below (e.g. Contentful delivers asset urls
  // as '//images.ctfassets.net/...', which is external).
  const isRelativePath = (href.startsWith('/') && !href.startsWith('//')) || href.startsWith('#');
  if (isRelativePath) {
    return true;
  }

  // For absolute URLs, parse and check the hostname
  const lowerHref = href.toLowerCase();
  if (lowerHref.startsWith('http://') || lowerHref.startsWith('https://') || href.startsWith('//')) {
    try {
      // Handle protocol-relative URLs
      const urlToParse = href.startsWith('//') ? `https:${href}` : href;
      const parsedUrl = new URL(urlToParse);
      const hostname = parsedUrl.hostname.toLowerCase();

      // Check for exact domain matches (with optional subdomains)
      // Allow common subdomains: www, enterprise, member, app, and environment-specific variations
      const internalDomainPattern =
        /^((www|enterprise|careers|member(-(qa|dev\d))?|app(-(qa|dev\d))?)\.)?((chime|1debit)\.com)$/;
      return internalDomainPattern.test(hostname);
    } catch {
      // If URL parsing fails, it's not a valid URL
      return false;
    }
  }

  // For other cases (e.g., "chime.com/page" without protocol), treat as relative
  // unless it contains suspicious patterns
  if (href.includes('.com.') || href.includes('.com/')) {
    // Potential subdomain spoofing attempt
    return false;
  }

  return false;
}

/**
 * Construct enrollment URL with proper query parameter preservation
 * @param baseUrl - The base enrollment URL (custom or default)
 * @param additionalParams - Optional additional parameters to add
 * @returns The enrollment URL with all query parameters preserved
 */
function constructEnrollmentUrlWithQueryParams(
  baseUrl: string,
  additionalParams?: Record<string, string>,
  options?: EnrollmentUrlContext,
): string {
  // When the caller provides explicit attribution/page params, use them
  // instead of reading from cookies and window.location (enables SSR).
  const useExplicitContext = options?.persistedAttribution !== undefined || options?.pageQueryParams !== undefined;

  // Helper function to add parameters to combined params
  const addParamsToCombined = (params: URLSearchParams, combinedParams: URLSearchParams): void => {
    params.forEach((value, key) => {
      combinedParams.set(key, value);
    });
  };

  // Get current page query parameters
  const persistedAttribution = useExplicitContext ? (options?.persistedAttribution ?? {}) : getPersistedAttribution();
  const pageParams = new URLSearchParams();
  if (options?.pageQueryParams) {
    Object.entries(options.pageQueryParams).forEach(([key, value]) => {
      pageParams.set(key, value);
    });
  } else if (!useExplicitContext && typeof window !== 'undefined') {
    const urlParams = new URLSearchParams(window.location.search);
    addParamsToCombined(urlParams, pageParams);
  }

  // Parse the CTA URL to extract any existing query parameters
  let ctaUrlWithoutParams = baseUrl;
  let ctaParams = new URLSearchParams();

  try {
    const fallbackOrigin = typeof window !== 'undefined' ? window.location.origin : 'https://www.chime.com';
    const url = new URL(baseUrl, fallbackOrigin);
    ctaParams = new URLSearchParams(url.search);
    ctaUrlWithoutParams = url.origin + url.pathname;
  } catch {
    // If URL parsing fails, assume no existing params
    ctaUrlWithoutParams = baseUrl;
  }

  // Combine all parameters: page params + CTA params + additional params
  const combinedParams = new URLSearchParams();

  // Add parameters in order of precedence (additional params override CTA, CTA overrides page, page overrides persisted)
  Object.entries(persistedAttribution).forEach(([key, value]) => {
    combinedParams.set(key, value);
  });
  addParamsToCombined(pageParams, combinedParams);
  addParamsToCombined(ctaParams, combinedParams);

  // Add additional params, currently used for promo codes
  if (additionalParams) {
    Object.entries(additionalParams).forEach(([key, value]) => {
      combinedParams.set(key, value);
    });
  }

  // Construct the final URL
  const queryString = combinedParams.toString();
  return queryString ? `${ctaUrlWithoutParams}?${queryString}` : ctaUrlWithoutParams;
}

function isSubdomainSlug(slug: string): boolean {
  // Match slugs that start with "enterprise/" or "/enterprise/" or any allowed subdomain
  const subdomainPattern = new RegExp(`^(/?(${ALLOWED_OVERRIDE_SUBDOMAINS.join('|')})/)`);
  return subdomainPattern.test(slug);
}

function getRelativePath(href: string): string {
  const isFullUrl = /^[^/]+?\.[^/]+/.test(href);

  if (isFullUrl || typeof window === 'undefined') return href;

  const hostname = window.location.hostname;

  // Dynamically create a regex pattern based on the allowed subdomains array
  const subDomainPattern = (allowedSubdomain: string) =>
    new RegExp(`^(/?(${allowedSubdomain}))|(.*[.-](${allowedSubdomain})([.-]|$))`);

  // Extract the subdomain from the hostname (e.g., "enterprise" from "enterprise.test.com" or "enterprise-test.com")
  const matchedSubdomain =
    ALLOWED_OVERRIDE_SUBDOMAINS.find(allowedSubdomain => subDomainPattern(allowedSubdomain).test(hostname)) ||
    hostname.split('.')[0] ||
    '';

  const normalizedHref = href.startsWith('/') ? href : `/${href}`;

  if (matchedSubdomain && normalizedHref.startsWith(`/${matchedSubdomain}`)) {
    // If the hostname's subdomain matches the slug's subdomain, strip it
    const stripped = href.replace(subDomainPattern(matchedSubdomain), '');
    return stripped.startsWith('/') ? stripped : `/${stripped}`;
  }

  // If no change was performed return the href unchanged
  return href;
}

function getEnrollmentUrl(): string {
  switch (appEnvironment) {
    case 'prod':
    case 'production':
      return 'https://app.chime.com/enroll/account';
    case 'local':
    case 'development':
      return 'https://app-qa.chime.com/enroll/account';
    default:
      return `https://app-${appEnvironment}.chime.com/enroll/account`;
  }
}

function getLinkType(href: string): 'social' | 'affiliate' | 'outbound' {
  // check if the link is a social link
  const socialLinksRegex = /https?:\/\/(www\.)?(facebook\.com|twitter\.com|x\.com|instagram\.com)/i;
  const socialMatch = href.match(socialLinksRegex);

  // check if the link is an affiliate link
  const affiliateLinksRegex =
    /https?:\/\/(www\.)?(\w+\.)?(affiliate|affiliates|ref|partner|track|clickbank|shareasale|cj|rakuten|impactradius|awin|linksynergy)\..+/i;
  const affiliateMatch = href.match(affiliateLinksRegex);

  return socialMatch ? 'social' : affiliateMatch ? 'affiliate' : 'outbound';
}

/**
 * Construct the href from LinkProps, handling internalPage
 */
export function constructHref(
  href?: string,
  internalPage?: { slug: string },
  enrollmentUrlContext?: EnrollmentUrlContext,
): string {
  if (internalPage) {
    return `/${internalPage.slug || ''}`;
  }

  const resolvedHref = href || '';
  if (resolvedHref && isEnrollmentUrl(resolvedHref)) {
    return constructEnrollmentUrlWithQueryParams(resolvedHref, undefined, enrollmentUrlContext);
  }

  if (isSubdomainSlug(resolvedHref)) {
    const stripped = getRelativePath(resolvedHref);
    return stripped.startsWith('/') ? stripped : `/${stripped}`;
  }

  return resolvedHref;
}

/**
 * Performs a full-page navigation. Kept as a helper so tests can mock it —
 * jsdom cannot perform or intercept location.href assignment.
 */
export function navigateTo(url: string): void {
  window.location.href = url;
}

const CAMPAIGN_AD_VALUES: Record<string, string> = {
  referral: 'ch_raf',
  joint_checking: 'ch_joint_account',
};

/**
 * Applies all enrollment-specific query parameters to a URL.
 * Both helpers always run to preserve all campaign tokens.
 * If a `last_campaign` cookie exists, its `ad` value wins.
 */
function applyEnrollmentParams(url: string): string {
  let result = url;
  result = addReferralParamsToUrl(result);
  result = addJointCheckingParamsToUrl(result);

  const lastCampaign = typeof window !== 'undefined' ? getCookie(LAST_CAMPAIGN_COOKIE) : undefined;
  const adValue = typeof lastCampaign === 'string' ? CAMPAIGN_AD_VALUES[lastCampaign] : undefined;

  if (adValue) {
    try {
      const urlObj = new URL(result, window.location.origin);
      urlObj.searchParams.set('ad', adValue);
      result = result.startsWith('/') ? urlObj.pathname + urlObj.search : urlObj.toString();
    } catch {
      // leave result as-is
    }
  }

  return result;
}

const ANON_ID_TOKEN = 'anon_id';
const ANON_ID_PLACEHOLDER = `[${ANON_ID_TOKEN}]`;

/**
 * Resolves the `[anon_id]` placeholder in a CTA URL against a supplied device id
 * (the Chime device id used to bucket experiments). Pure and value-parameterized
 * so the same logic drives both server-side resolution during the link transform
 * (where the id comes from the request cookie via `getPageContext().anonymousId`)
 * and the client-side fallback below.
 *
 * Detection runs against the parsed query params, so the placeholder is matched
 * whether it arrives literal (`deep_link_sub1=[anon_id]`) or percent-encoded
 * (`deep_link_sub1=%5Banon_id%5D`, any hex case): anything that round-trips the
 * href through `URLSearchParams` (a URL builder, or the enrollment-param helpers
 * should they ever touch this URL) encodes the brackets, so we can't assume the
 * placeholder survives verbatim. When no device id is available the placeholder
 * params are dropped so we never navigate with an unresolved value. No-ops when
 * the placeholder is absent or the URL cannot be parsed.
 */
function resolveAnonIdInUrl(href: string, deviceId: string | null | undefined): string {
  // Cheap guard so the page walk skips non-matching URLs without constructing a
  // URL. Matches both the literal `[anon_id]` and the percent-encoded
  // `%5Banon_id%5D` form, since both contain the raw token.
  if (!href.includes(ANON_ID_TOKEN)) {
    return href;
  }

  let url: URL;
  try {
    url = new URL(href);
  } catch {
    return href;
  }

  // searchParams decodes values, so an encoded `%5Banon_id%5D` compares equal
  // to the literal placeholder here.
  const placeholderKeys: string[] = [];
  url.searchParams.forEach((value, key) => {
    if (value === ANON_ID_PLACEHOLDER) {
      placeholderKeys.push(key);
    }
  });

  if (placeholderKeys.length === 0) {
    return href;
  }

  const resolvedId = deviceId || null;

  placeholderKeys.forEach(key => {
    if (resolvedId) {
      url.searchParams.set(key, resolvedId);
    } else {
      url.searchParams.delete(key);
    }
  });

  return url.toString();
}

/**
 * Client-side fallback for `[anon_id]` resolution. Primary resolution now runs
 * server-side in the link transform (`resolveAnonIdInUrl` via
 * `transformPageWithSearchParams`), so on transform-covered routes the SSR href
 * carries no surviving placeholder and this is a no-op. It still resolves at
 * click time on paths that bypass the server transform (agreement/policies
 * pages) and on client-side SPA navigations, reading the browser-only device-id
 * cookie.
 */
function resolveAnonIdToken(href: string): string {
  // Skip the cookie read entirely when there is no placeholder to resolve,
  // preserving the "no placeholder -> no cookie read" behavior.
  if (!href.includes(ANON_ID_TOKEN)) {
    return href;
  }

  const deviceId = getCookie(DEVICE_ID_COOKIE);
  return resolveAnonIdInUrl(href, typeof deviceId === 'string' ? deviceId : null);
}

export {
  isEnrollmentUrl,
  isOutboundLink,
  isEngagementLink,
  isStoreUrl,
  isInternalLink,
  getInteractionType,
  getLinkType,
  getCTAType,
  getEnrollmentUrl,
  isSubdomainSlug,
  getRelativePath,
  getSecondaryCTAIndex,
  constructEnrollmentUrlWithQueryParams,
  applyEnrollmentParams,
  ANON_ID_TOKEN,
  resolveAnonIdInUrl,
  resolveAnonIdToken,
};
